import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import isEmpty from "lodash/isEmpty";

//importing custom modules
import { Button } from "../../components";
import Input from "../../components/UserInput/Input";
import { newUserSchema } from "../../yup";

// importing styles
import style from "./nurse.module.css";
import { colors } from "../../theme/colors";

// importing api
import {
  useUpdateUserMutation,
  useGetUserDetailsQuery,
} from "../../services/api";
import { LabelBox } from "../../components";
import { SingleSelectInput } from "../../components/SelectInput";
import { Prefix } from "../../utils/constants";
import { getErrorMessage } from "../../utils/common";

type FormState = typeof newUserSchema.__outputType;

/**
 * Renders the nurse edit form and updates nurse details for the given route id.
 *
 * Returns:
 *   JSX.Element React component rendering the Edit Nurse page.
 *
 * Notes:
 *   Requires a valid numeric `id` from the route params. If absent or invalid,
 *   the form submission is blocked with an error message.
 */
const EditNurse = () => {
  const { id } = useParams();
  const navigation = useNavigate();
  const [updateUser] = useUpdateUserMutation();

  // Get Nurse Details mutation hook
  const {
    data: nurseDetails,
    isLoading,
    isFetching,
  } = useGetUserDetailsQuery(id ?? "");

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormState>({
    resolver: yupResolver(newUserSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (!isEmpty(nurseDetails)) {
      reset({
        prefix: nurseDetails.prefix ?? "",
        given_name: (nurseDetails)?.given_name ?? "",
        surname: (nurseDetails)?.surname ?? "",
        mail: (nurseDetails)?.mail ?? "",
        phone:
          (nurseDetails)?.phone ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nurseDetails]);

  const onSubmit = async (values: FormState) => {
    const parsedId = Number(id);
    if (!id || Number.isNaN(parsedId)) {
      setMessage({
        type: "error",
        text: "Invalid nurse id.",
      });
      return;
    }

    try {
      setLoading(true);

      let payload: EditUserPayload = {
        id: parsedId,
        given_name: values?.given_name,
        surname: values?.surname,
        phone: values?.phone,
      };

      if (values.prefix && values.prefix !== "") {
        payload.prefix = values.prefix;
      }

      await updateUser({ ...payload }).unwrap();
      setLoading(false);
      setMessage({
        type: "success",
        text: "Nurse updated successfully.",
      });
      setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {(isFetching || isLoading) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "60vh",
              width: "calc(100vw - 400px)",
              zIndex: 999,
              backgroundColor: "transparent",
              position: "absolute",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <Box>
          <Typography className={style.detailHeading}>Nurse Details</Typography>
          <Box className={style.flexTop} sx={{ marginTop: "20px" }}>
            <Box className={style.row}>
              <SingleSelectInput
                placeholder="Select Prefix"
                label="Prefix"
                register={register}
                name="prefix"
                errors={errors}
                data={Prefix}
                value={watch("prefix") ?? ""}
              />
            </Box>

            <Box className={style.row}>
              <Input
                placeholder="First Name"
                label="First Name"
                required
                register={register}
                name="given_name"
                errors={errors}
              />
              <Input
                placeholder="Last Name"
                label="Last Name"
                required
                register={register}
                name="surname"
                errors={errors}
              />
            </Box>

            <Box className={style.row}>
              <Input
                placeholder="Email Address"
                label="Email Address"
                required
                register={register}
                name="mail"
                errors={errors}
                disabled={true}
              />
              <Input
                placeholder="Phone Number"
                label="Phone Number"
                register={register}
                name="phone"
                errors={errors}
              />

            </Box>
            {message && (
              <Box mt={2}>
                <LabelBox
                  boxProps={{
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                  }}
                  data={message}
                  onClose={() => setMessage(null)}
                />
              </Box>
            )}
            <Box className={style.buttons}>
              <Button
                sx={{
                  width: "200px",
                  height: "55px",
                }}
                variant="outlined"
                onClick={() => navigation(-1)}
                label="Cancel"
              />
              <Button
                loading={loading}
                sx={{
                  width: "200px",
                  height: "55px",
                  ":hover": { backgroundColor: colors.primary },
                }}
                variant="contained"
                type="submit"
                label="Save"
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EditNurse;
